// ============================================================
// CaseOverview.tsx
// The case page LANDING section (Stage 1 shell — approved mockup "CA132B"
// Overview). Read-only summary over REAL case data; the only writes it can
// trigger are the SAME existing actions the old page had:
//   • "Continue: <section>" → the same flush-then-switch nav as a nav click
//   • "Finalise setup" → the store's existing finaliseSetup (as Case control)
// Contents (mockup order):
//   1. reserved "PI stages — coming" strip (dashed; real stages = Callum)
//   2. Case setup: completedSections ring + Draft→Open→Closed lifecycle +
//      the visible setup chain (✓ ◐ ○ 🔒 !) + Continue / Finalise
//   3. stat tiles: open deadlines · documents · witnesses · linked cases
//   4. Next deadlines · Key dates (limitation deadline or est.)
//   5. Client snapshot · Claim snapshot
//   6. Recent activity (case History audit log; firm visibility gate)
// ============================================================
import { useLivePermissions } from '../../hooks/useLivePermissions'
import React, { useEffect, useState } from 'react'
import { SKELETON_BAR } from '../../hooks/useLoadPhase'
import { useCasevoStore } from '../../store/useCasevoStore'
import { navigateTo } from '../../navigation/navigate'
import { listAuditLogsApi } from '../../api/casevoApi'
import { useCaseChange } from '../../utils/caseChangeEvents'
import type { AuditLogEntry } from '../../types/auditLog.types'
import { renderActionSentence } from '../../utils/auditSentence'
import { relativeTime } from '../../utils/relativeTime'
import ActorAvatar from '../shared/ActorAvatar'
import Avatar from '../shared/Avatar'
import { BTN_PRIMARY } from '../../styles/buttons'
import type { CompletableSection } from '../../store/useSectionCompletion'
import type { SectionStatus } from '../../utils/sectionFlow'
import type { CaseSectionKey } from '../../types/case.types'
import { CASE_NAV_INDEX } from '../../constants/caseNav.constants'
import { CASE_CARD, CIcon, type CIconName, CaseStatusMark, fmtDay, tileParts, todayYmd, daysBetween, relDays, yearsMonthsLeft, ymdOf } from './caseUi'
import { limitationOf, limitationPassedOf, noTimeLimitOf, openDeadlinesOf, injuryAreasOf, accidentDescriptionOf, accidentPlaceOf } from './caseFacts'
import { childWording, estimateBadge, estimateBasisText, estimateStartOf, LIMITATION_PASSED } from '../../utils/limitationRules'
import { claimFactsOf } from '../../utils/caseClaim'
import { textDateToIso } from '../../utils/civilDate'

interface CaseOverviewProps {
  visibleSequential: CompletableSection[]
  statusByKey: Partial<Record<string, SectionStatus>>
  completedSections: Set<CompletableSection>
  allSectionsComplete: boolean
  // Keys the user can open right now (visible + not locked).
  canOpen: (key: CaseSectionKey) => boolean
  onOpen: (key: CaseSectionKey) => void
  canViewHistory: boolean
}

// ── exact mockup classes ──
const CH = 'flex items-center gap-[12px] px-[20px] pt-[16px] pb-[12px]'
const CH_IB = 'w-8 h-8 rounded-[10px] bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] grid place-items-center shrink-0'
const CH_T = 'm-0 text-[15px] font-semibold text-casevo-forest'
const CH_S = 'text-[12px] text-casevo-text-soft mt-px'
const CH_R = 'ml-auto shrink-0 inline-flex items-center gap-[4px] text-[12.5px] font-semibold text-casevo-brand dark:text-[#8fd6b0] whitespace-nowrap hover:underline focus:outline-none casevo-focus rounded-[3px]'
const CB = 'px-[20px] pb-[18px]'
const KV_K = 'text-[11px] text-casevo-text-soft font-semibold'
const KV_V = 'text-[13.5px] font-semibold mt-[2px] text-casevo-forest [overflow-wrap:anywhere]'
const SOFT_V = 'font-normal italic text-casevo-text-soft'
const PI_STAGES = ['Instructed', 'Investigation', 'Liability', 'Medical evidence', 'Quantum', 'Negotiation', 'Settlement']
// Stat-tile tones (light / dark literal pairs — mockup .tr .ts .tv .tg).
const TONE = {
  red: 'bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]',
  sky: 'bg-[#e5f0fa] text-[#2f6aa3] dark:bg-[rgba(124,174,230,0.15)] dark:text-[#9cc3ef]',
  violet: 'bg-[#efe9f8] text-[#6a4c9c] dark:bg-[rgba(176,148,226,0.16)] dark:text-[#c4adf0]',
  green: 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]',
  warn: 'bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]',
  soft: 'bg-casevo-input text-casevo-text-soft',
} as const

const CardHead: React.FC<{ icon: CIconName; title: string; sub?: React.ReactNode; right?: React.ReactNode }> = ({ icon, title, sub, right }) => (
  <div className={CH}>
    <span className={CH_IB}><CIcon name={icon} size={16} /></span>
    <div className="min-w-0">
      <h3 className={CH_T}>{title}</h3>
      {sub && <div className={CH_S}>{sub}</div>}
    </div>
    {right}
  </div>
)
const KV: React.FC<{ k: string; full?: boolean; children: React.ReactNode }> = ({ k, full, children }) => (
  <div className={`min-w-0 ${full ? 'col-span-2' : ''}`}>
    <div className={KV_K}>{k}</div>
    <div className={KV_V}>{children}</div>
  </div>
)
const none = <span className={SOFT_V}>Not recorded</span>
// A summary row that jumps to its SECTION (section-level, not the exact item —
// the target sections don't support item deep-links yet). Renders a full-
// width button when the section can be opened, else the plain row.
const RowLink: React.FC<{
  to: CaseSectionKey
  canOpen: (key: CaseSectionKey) => boolean
  onOpen: (key: CaseSectionKey) => void
  label: string
  className: string
  children: React.ReactNode
}> = ({ to, canOpen, onOpen, label, className, children }) =>
  canOpen(to) ? (
    <button type="button" onClick={() => onOpen(to)} aria-label={label} title={label}
      className={`${className} w-full text-left -mx-[8px] px-[8px] !w-[calc(100%+16px)] rounded-[8px] transition-colors hover:bg-[color-mix(in_srgb,var(--casevo-mint)_40%,transparent)] focus:outline-none casevo-focus`}>
      {children}
    </button>
  ) : (
    <div className={className}>{children}</div>
  )

// Audit action → activity chip (mockup .chip).
const chipFor = (action: string): { label: string; tone: string } => {
  if (action.startsWith('case_deadline')) return { label: 'Deadline', tone: TONE.red }
  if (action.startsWith('task')) return { label: 'Task', tone: TONE.green }
  if (action.includes('witness')) return { label: 'Witness', tone: TONE.violet }
  if (action.includes('file') || action.includes('document')) return { label: 'Document', tone: TONE.sky }
  return { label: 'Case', tone: TONE.soft }
}

const CaseOverview: React.FC<CaseOverviewProps> = ({
  visibleSequential, statusByKey, completedSections, allSectionsComplete, canOpen, onOpen, canViewHistory,
}) => {
  const currentCase = useCasevoStore((s) => s.currentCase)
  const deadlinesMap = useCasevoStore((s) => s.caseDeadlines)
  const witnessesMap = useCasevoStore((s) => s.witnesses)
  const filesMap = useCasevoStore((s) => s.files)
  const finaliseSetup = useCasevoStore((s) => s.finaliseSetup)
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  const caseId = currentCase?.id ?? null

  // Recent activity — the case History audit log (same endpoint/gate as
  // CaseHistorySection), newest 4. Only fetched when the viewer may see it.
  const [activity, setActivity] = useState<AuditLogEntry[] | null>(null)
  const [activityError, setActivityError] = useState(false)
  useEffect(() => { void loadFirmUsers() }, [loadFirmUsers])
  // ★ Live permission change: clear the recent rows at once if a money right
  // was lost (bill £ can be in them), then re-read.
  const [activityNonce, setActivityNonce] = useState(0)
  useLivePermissions((narrowed) => { if (narrowed) setActivity(null); setActivityNonce((n) => n + 1) })
  useEffect(() => {
    if (!caseId || !canViewHistory) return
    let cancelled = false
    setActivity(null)
    setActivityError(false)
    listAuditLogsApi(1, 4, undefined, undefined, caseId)
      .then((r) => { if (!cancelled) setActivity(r.data) })
      .catch(() => { if (!cancelled) setActivityError(true) })
    return () => { cancelled = true }
  }, [caseId, canViewHistory, activityNonce])
  // (D12) Someone else changed anything on the case → the recent rows re-read
  // quietly (no skeleton flash).
  useCaseChange(caseId, '*', (_k, info) => {
    if (!info.remote || !caseId || !canViewHistory) return
    listAuditLogsApi(1, 4, undefined, undefined, caseId).then((r) => { setActivity(r.data); setActivityError(false) }).catch(() => undefined)
  })

  if (!currentCase) return null
  const c = currentCase
  const today = todayYmd()
  const deadlines = deadlinesMap[c.id]
  const open = openDeadlinesOf(deadlines)
  const overdue = open.filter((d) => daysBetween(today, ymdOf(d.dueDate) ?? today) < 0).length
  const soon = open.filter((d) => { const n = daysBetween(today, ymdOf(d.dueDate) ?? today); return n >= 0 && n <= 3 }).length
  // Counts: witnesses / deadlines / files are SEPARATE store slices filled
  // by setCurrentCaseById AFTER the case itself renders. `undefined` = still
  // loading (never shown as a misleading 0); `[]` = loaded and genuinely
  // empty (failed loads also land as [] — same as the sections show).
  // Linked cases come inside the case payload (caseLinks), so are ready.
  const deadlinesLoaded = deadlines !== undefined
  const documentCount: number | null = filesMap[c.id] === undefined ? null : filesMap[c.id].length
  const witnessCount: number | null = witnessesMap[c.id] === undefined ? null : witnessesMap[c.id].length
  const linkedCount = c.caseLinks?.length ?? 0

  // ── setup ──
  const total = visibleSequential.length
  const done = visibleSequential.filter((k) => completedSections.has(k)).length
  const nextKey = visibleSequential.find((k) => !completedSections.has(k) && statusByKey[k] !== 'locked')
  const finalised = c.caseControl.setupFinalised
  const closed = !!c.caseControl.closeDate
  const lifecycle: 'Draft' | 'Open' | 'Closed' = closed ? 'Closed' : finalised ? 'Open' : 'Draft'
  const pct = total > 0 ? done / total : 0
  const labelOf = (k: string): string => CASE_NAV_INDEX[k]?.label ?? k

  // ── dates ──
  // Shared date rule (ISO or UK D/M/YYYY) — the same reading as the estimate.
  const accYmd = textDateToIso(c.accidentDetails.accidentDate)
  const openedYmd = ymdOf(c.caseControl.openDate)
  const createdYmd = ymdOf(c.createdAt)
  // Until deadlines load, don't show the accident+3y ESTIMATE (it may be a
  // real Limitation-date deadline a moment later) — show a pending state.
  const lim = deadlines !== undefined ? limitationOf(c, deadlines) : null
  // (B2b) Confirmed "no time limit" (child sexual abuse, s.11ZA).
  const noLimit = deadlines !== undefined && !lim ? noTimeLimitOf(c) : null
  // (B2b fix 2) The limitation date (saved or estimated) is before today.
  const limPassed = limitationPassedOf(c, deadlines, today)

  // ── client ──
  const cd = c.clientDetails
  const clientFull = [cd.title, cd.forename, cd.surname].map((s) => (s ?? '').trim()).filter(Boolean).join(' ') || 'Client not named yet'
  const age = (() => {
    const d = ymdOf(cd.dateOfBirth)
    if (!d) return null
    const [y, m, dd] = d.split('-').map(Number)
    const [ty, tm, td] = today.split('-').map(Number)
    return ty - y - (tm < m || (tm === m && td < dd) ? 1 : 0)
  })()
  const address = [cd.addressLine1, cd.addressLine2, cd.addressTown, cd.addressCounty, cd.addressPostcode].map((s) => (s ?? '').trim()).filter(Boolean).join(', ')
  const openClient = async (): Promise<void> => {
    const clientId = c.client?.id
    if (!clientId) return
    // navigateTo flushes pending edits, asks the unsaved-changes guard and
    // records the step (Back returns to this case + section).
    await navigateTo({ view: 'clientDetail', clientId })
  }

  // ── claim ──
  const areas = injuryAreasOf(c)
  const accDesc = accidentDescriptionOf(c)
  const place = accidentPlaceOf(c)
  const def = c.defendant
  const tpi = c.tpi

  const link = (key: CaseSectionKey, label: string): React.ReactNode =>
    canOpen(key) ? (
      <button type="button" className={CH_R} onClick={() => onOpen(key)}>{label}<CIcon name="right" size={13} /></button>
    ) : null

  return (
    <div className="leading-[normal] text-casevo-forest">
      {/* ── head (mockup .ovh) ── */}
      <div className="flex items-end gap-[12px] mt-[2px] mb-[14px] flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-[#9a7b1e] dark:text-casevo-light">Case home</div>
          <h2 className="mt-[2px] mb-0 text-[24px] font-semibold tracking-[-0.01em] text-casevo-forest">Overview</h2>
        </div>
        <span className="flex-1" />
        <span className="text-[12.5px] text-casevo-text-soft">Last saved {relativeTime(c.updatedAt)}</span>
      </div>

      {/* ── 1. reserved PI stages strip ── */}
      <div className="border-2 border-dashed border-[color-mix(in_srgb,var(--casevo-brand)_40%,var(--casevo-border))] rounded-[14px] px-[20px] pt-[16px] pb-[18px] bg-[color-mix(in_srgb,var(--casevo-mint)_28%,transparent)] mb-[16px]">
        <div className="flex items-center gap-[10px] mb-[14px] flex-wrap">
          <span className="text-[11px] tracking-[0.16em] uppercase font-bold text-casevo-brand dark:text-[#8fd6b0]">PI stages</span>
          <span className="text-[10.5px] font-bold tracking-[0.06em] uppercase px-[8px] py-[2px] rounded-full bg-casevo-surface border border-dashed border-casevo-border text-casevo-text-soft">Coming soon</span>
          <span className="sm:ml-auto text-[12px] text-casevo-text-soft">Reserved for the PI stage engine. The current stage will show here, with its next action.</span>
        </div>
        <div className="hidden sm:flex items-center" aria-hidden="true">
          {PI_STAGES.map((st, i) => (
            <React.Fragment key={st}>
              {i > 0 && <span className="flex-1 h-0 border-t-2 border-dashed border-[color-mix(in_srgb,var(--casevo-brand)_30%,var(--casevo-border))] -mx-[14px] mb-[22px]" />}
              <div className="flex flex-col items-center gap-[7px] w-[96px] shrink-0 text-center">
                <i className="not-italic w-[26px] h-[26px] rounded-full border-2 border-dashed border-[color-mix(in_srgb,var(--casevo-brand)_45%,var(--casevo-border))] grid place-items-center text-[11px] font-bold text-casevo-text-soft bg-casevo-surface">{i + 1}</i>
                <span className="text-[12px] text-casevo-text-soft leading-[1.2]">{st}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── 2. case setup ── */}
      <section className={CASE_CARD} aria-label="Case setup">
        <CardHead icon="task" title="Case setup" sub="Complete each section in order to finalise the case and move it from Draft to Open." />
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-[22px] items-center px-[20px] pt-[4px] pb-[18px]">
          {/* Progress ring — 96px, 3px stroke (r 16.5 → circumference 103.67).
              Text is a centred flex column: the done count big (26/700,
              tabular, leading-none) with a smaller soft "/total", then a
              10px uppercase label — every line leading-none so the block
              sits on the ring's true centre. */}
          <div className="relative w-24 h-24 mx-auto sm:mx-0 shrink-0" role="img" aria-label={`${done} of ${total} sections complete`}>
            <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90" aria-hidden="true">
              <circle cx="18" cy="18" r="16.5" fill="none" stroke="var(--casevo-border-soft)" strokeWidth="3" />
              <circle cx="18" cy="18" r="16.5" fill="none" stroke="var(--casevo-brand)" strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${(pct * 103.67).toFixed(2)} 103.67`} className={pct === 0 ? 'opacity-0' : 'dark:stroke-[#52a87f]'} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center leading-none" aria-hidden="true">
              <span className="flex items-baseline tabular-nums">
                <span className="text-[26px] font-bold tracking-[-0.02em] text-casevo-forest">{done}</span>
                <span className="text-[13px] font-semibold text-casevo-text-soft ml-[1px]">/{total}</span>
              </span>
              <span className="mt-[5px] text-[10px] font-semibold tracking-[0.08em] uppercase text-casevo-text-soft">sections</span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-[6px] text-[12px] font-semibold mb-[10px] flex-wrap">
              {(['Draft', 'Open', 'Closed'] as const).map((s, i) => (
                <React.Fragment key={s}>
                  {i > 0 && <span className="text-casevo-text-soft" aria-hidden="true">→</span>}
                  <span className={`px-[10px] py-[3px] rounded-full border ${lifecycle === s
                    ? 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] border-[color-mix(in_srgb,var(--casevo-brand)_35%,transparent)]'
                    : 'bg-casevo-input text-casevo-text-soft border-casevo-border-soft'}`}>{s}</span>
                </React.Fragment>
              ))}
              <span className="ml-[8px] font-medium text-casevo-text-soft">
                {closed ? `Closed ${fmtDay(c.caseControl.closeDate)}` : finalised ? 'Setup finalised: the case is open' : 'Finalise setup moves the case to Open'}
              </span>
            </div>
            <div className="flex flex-wrap gap-[6px]">
              {visibleSequential.map((k) => {
                const st = statusByKey[k] ?? 'empty'
                const isNext = k === nextKey && !finalised
                const locked = st === 'locked'
                return (
                  <button key={k} type="button" disabled={locked} onClick={() => onOpen(k)} title={locked ? 'Locked until the section before it is complete' : `Open ${labelOf(k)}`}
                    className={`inline-flex items-center gap-[7px] text-[12.5px] px-[10px] py-[6px] rounded-[9px] border transition-colors focus:outline-none casevo-focus ${
                      isNext
                        ? 'border-casevo-brand bg-[color-mix(in_srgb,var(--casevo-mint)_55%,transparent)] font-bold text-casevo-forest'
                        : locked
                          ? 'border-casevo-border-soft bg-casevo-surface text-casevo-text-soft cursor-not-allowed'
                          : 'border-casevo-border-soft bg-casevo-surface text-casevo-forest hover:border-casevo-mid'
                    }`}>
                    <CaseStatusMark status={st} size={14} />{labelOf(k)}
                  </button>
                )
              })}
              {total === 0 && <span className="text-[12.5px] text-casevo-text-soft italic">All setup sections are hidden. Use Manage sections to show them.</span>}
            </div>
            <div className="flex items-center gap-[10px] mt-[14px] flex-wrap">
              {!finalised && nextKey && (
                <button type="button" onClick={() => onOpen(nextKey)} className={`${BTN_PRIMARY} text-[14px] px-[16px] py-[9px] rounded-[9px]`}>
                  Continue: {labelOf(nextKey)} <CIcon name="arrow" size={15} />
                </button>
              )}
              {!finalised && !closed && (
                <button type="button" onClick={finaliseSetup} disabled={!allSectionsComplete}
                  className={allSectionsComplete
                    ? `${BTN_PRIMARY} text-[14px] px-[16px] py-[9px] rounded-[9px]`
                    : 'inline-flex items-center gap-[7px] text-[13px] font-semibold px-[14px] py-[8px] rounded-[9px] border border-casevo-border text-casevo-text-soft bg-casevo-surface cursor-not-allowed'}>
                  {!allSectionsComplete && <CIcon name="lock" size={14} />}Finalise setup
                </button>
              )}
              <span className="text-[12px] text-casevo-text-soft">
                {finalised ? `${done} of ${total} sections signed off` : allSectionsComplete ? 'Every section is complete' : `Unlocks when all ${total} sections are complete`}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. stat tiles ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[12px] my-[16px]">
        {/* Each tile jumps to its section (same flush-then-switch as a nav
            click). A tile whose section is hidden or still locked renders
            static, with the reason as a tooltip. */}
        {([
          ['flag', deadlinesLoaded ? open.length : null, `open deadline${open.length === 1 ? '' : 's'}${overdue ? ` · ${overdue} overdue` : soon ? ` · ${soon} due soon` : ''}`, TONE.red, 'deadlines'],
          ['file', documentCount, `document${documentCount === 1 ? '' : 's'}`, TONE.sky, 'documents'],
          ['eye', witnessCount, `witness${witnessCount === 1 ? '' : 'es'}`, TONE.violet, 'witnesses'],
          ['link', linkedCount, `linked case${linkedCount === 1 ? '' : 's'}`, TONE.green, 'linkedCases'],
        ] as [CIconName, number | null, string, string, CaseSectionKey][]).map(([ic, n, lbl, tone, key]) => {
          const body = (
            <>
              <span className={`w-[38px] h-[38px] rounded-[11px] grid place-items-center shrink-0 ${tone}`}><CIcon name={ic} size={18} /></span>
              <div className="min-w-0 flex-1">
                {n === null ? (
                  // Loading — a quiet skeleton the height of the number (24px),
                  // so nothing shifts when the real count lands.
                  <span role="status" aria-label="Loading" className="block w-[30px] h-[20px] my-[2px] rounded-[6px] bg-casevo-border-soft animate-pulse" />
                ) : (
                  <b className="block text-[22px] font-bold tracking-[-0.01em] leading-[1.1]">{n}</b>
                )}
                <span className="text-[12px] text-casevo-text-soft">{lbl}</span>
              </div>
            </>
          )
          const tile = `${CASE_CARD} flex items-center gap-[12px] px-[16px] py-[14px] text-left`
          return canOpen(key) ? (
            <button key={ic} type="button" onClick={() => onOpen(key)} aria-label={`${n === null ? 'Loading' : n} ${lbl} — open ${labelOf(key)}`}
              className={`${tile} group transition-[border-color,box-shadow] hover:border-casevo-mid focus:outline-none casevo-focus`}>
              {body}
              <CIcon name="right" size={15} className="text-casevo-text-soft opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
            </button>
          ) : (
            <div key={ic} className={tile} title={`${labelOf(key)} is hidden or locked`}>{body}</div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[16px]">
        {/* ── 4a. next deadlines ── */}
        <section className={CASE_CARD}>
          <CardHead icon="flag" title="Next deadlines" sub="Soonest open first" right={link('deadlines', 'All deadlines')} />
          <div className={CB}>
            {/* ★ NO LAYOUT SHIFT: three placeholders shaped exactly like the real
                rows (same paddings, date tile, two text lines) — was two short
                bars that the real rows then outgrew, pushing the cards below. */}
            {!deadlinesLoaded && (
              <div aria-busy="true" aria-label="Loading deadlines">
                {[0, 1, 2].map((i) => (
                  <div key={i} aria-hidden="true" className={`flex items-center gap-[12px] ${i === 0 ? 'pt-[2px] pb-[10px]' : 'py-[10px] border-t border-casevo-border-soft'}`}>
                    <div className="w-[44px] h-[41px] rounded-[9px] bg-casevo-input motion-safe:animate-pulse shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px]"><span className={`${SKELETON_BAR} h-3 w-40`} /></div>
                      <div className="text-[12px]"><span className={`${SKELETON_BAR} h-2.5 w-28`} /></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {deadlinesLoaded && open.length === 0 && <p className="m-0 text-[13px] text-casevo-text-soft italic">No open deadlines on this case.</p>}
            {open.slice(0, 3).map((d, i) => {
              const ymd = ymdOf(d.dueDate) ?? today
              const n = daysBetween(today, ymd)
              const { mon, day } = tileParts(ymd)
              const tone = n < 0 ? 'red' : n <= 3 ? 'warn' : 'ok'
              const notes = (d.notes ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
              return (
                <RowLink key={d.id} to="deadlines" canOpen={canOpen} onOpen={onOpen} label={`${d.deadlineType.name}, ${relDays(n)}: open Deadlines`}
                  className={`flex items-center gap-[12px] ${i === 0 ? 'pt-[2px] pb-[10px]' : 'py-[10px] border-t border-casevo-border-soft'}`}>
                  <div className={`w-[44px] text-center rounded-[9px] py-[5px] text-[10px] font-bold uppercase leading-[1.15] shrink-0 ${tone === 'red' ? TONE.red : tone === 'warn' ? TONE.warn : 'bg-casevo-input text-casevo-text-soft'}`}>
                    {mon}<b className={`block text-[16px] ${tone === 'ok' ? 'text-casevo-forest' : ''}`}>{day}</b>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold truncate">{d.deadlineType.name}</div>
                    <div className="text-[12px] text-casevo-text-soft truncate">{relDays(n)}{notes ? ` · ${notes}` : ''}</div>
                  </div>
                  <span className={`text-[10.5px] font-bold px-[8px] py-[2px] rounded-full whitespace-nowrap ${tone === 'red' ? TONE.red : tone === 'warn' ? TONE.warn : 'bg-casevo-input text-casevo-text-soft'}`}>
                    {tone === 'red' ? 'Overdue' : tone === 'warn' ? (n === 0 ? 'Today' : 'Due soon') : `${n} days`}
                  </span>
                </RowLink>
              )
            })}
          </div>
        </section>

        {/* ── 4b. key dates ── */}
        <section className={CASE_CARD}>
          <CardHead icon="cal" title="Key dates" sub="From accident details and deadlines" right={lim?.estimated ? link('deadlines', 'Add limitation') : null} />
          <div className={CB}>
            <div className="relative grid grid-cols-4 mt-[4px] before:content-[''] before:absolute before:left-[12%] before:right-[12%] before:top-[11px] before:h-[2px] before:bg-casevo-border-soft">
              {([
                ['Accident', accYmd, accYmd ? `${-daysBetween(today, accYmd)} days ago` : 'Not recorded', 'past'],
                [openedYmd ? 'Opened' : 'Created', openedYmd ?? createdYmd, finalised ? 'Case open' : 'Draft', 'past'],
                ['Today', today, lifecycle, 'today'],
                ['Limitation', lim?.ymd ?? null, lim ? `${yearsMonthsLeft(today, lim.ymd)}${lim.estimated && lim.basis === 'noDob' ? ` · ${estimateBadge(lim.basis)}` : ''}` : noLimit ? 'No time limit (s.11ZA)' : deadlines !== undefined ? 'Not set' : 'Loading…', 'lim'],
              ] as [string, string | null, string, 'past' | 'today' | 'lim'][]).map(([k, ymd, sub, kind]) => (
                <div key={k} className="text-center relative">
                  <i className={`block w-[14px] h-[14px] rounded-full mx-auto mt-[4px] mb-[8px] border-[3px] ${
                    kind === 'today' ? 'bg-casevo-brand border-casevo-brand' : kind === 'lim' ? 'bg-casevo-surface border-[#8a6212] dark:border-[#dcbb72]' : ymd ? 'bg-casevo-surface border-casevo-brand' : 'bg-casevo-surface border-casevo-border'}`} />
                  <div className="text-[11px] text-casevo-text-soft font-semibold">
                    {k}
                    {kind === 'lim' && lim?.estimated && <span className="ml-[4px] whitespace-nowrap text-[10px] font-bold tracking-[0.06em] uppercase px-[5px] py-[1px] rounded-[4px] border border-[#8a6212] text-[#8a6212] dark:border-[#dcbb72] dark:text-[#dcbb72]">est.</span>}
                  </div>
                  <div className="text-[13.5px] font-bold mt-[2px]">{ymd ? fmtDay(ymd) : '—'}</div>
                  <div className="text-[11.5px] text-casevo-text-soft">{sub}</div>
                  {kind === 'lim' && limPassed && <div role="status" className="text-[11.5px] font-semibold text-casevo-danger mt-[2px]">{LIMITATION_PASSED}</div>}
                </div>
              ))}
            </div>
            {lim?.estimated && (
              <div className={`flex gap-[10px] mt-[18px] px-[13px] py-[11px] rounded-[10px] text-[12.5px] leading-[1.45] ${TONE.warn}`}>
                <CIcon name="scale" size={16} className="mt-px" />
                <div><b className="font-bold">Limitation is an estimate.</b> No “Limitation date” deadline is set, so this shows {lim.basis === 'child'
                  ? childWording(estimateStartOf(accYmd, claimFactsOf(c.claim, c.protectedParty)))
                  : lim.basis === 'noDob' ? 'the accident date + 3 years — there’s no date of birth, so a child’s later date can’t be worked out' : lim.basis === 'adult' ? 'the accident date + 3 years' : estimateBasisText(lim.basis)}. Adding the deadline replaces the estimate everywhere, and only real deadlines are alerted on.</div>
              </div>
            )}
          </div>
        </section>

        {/* ── 5a. client snapshot ── */}
        <section className={CASE_CARD}>
          <CardHead icon="user" title="Client" sub="Summary from Client details" right={link('clientDetails', 'Edit')} />
          <div className={CB}>
            <div className="flex items-center gap-[12px] pb-[12px] mb-[12px] border-b border-casevo-border-soft">
              <Avatar size="md" firstName={cd.forename} lastName={cd.surname} />
              <div className="min-w-0">
                <div className="text-[15px] font-bold truncate">{clientFull}</div>
                <div className="text-[12px] text-casevo-text-soft">{c.matterNumber != null ? `Matter ${c.matterNumber}` : 'Client on this case'}</div>
              </div>
              {c.client?.id && (
                <button type="button" onClick={() => { void openClient() }} className={CH_R}>Open client<CIcon name="right" size={13} /></button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-[18px] gap-y-[12px]">
              <KV k="Date of birth">{ymdOf(cd.dateOfBirth) ? <>{fmtDay(cd.dateOfBirth)}{age != null && <span className="font-medium text-[12px] text-casevo-text-soft"> · {age}</span>}</> : none}</KV>
              <KV k="Phone">{cd.mainContactNumber.trim() || none}</KV>
              <KV k="Email" full>{cd.emailAddress.trim() || none}</KV>
              <KV k="Address" full>{address || none}</KV>
            </div>
          </div>
        </section>

        {/* ── 5b. claim snapshot ── */}
        <section className={CASE_CARD}>
          <CardHead icon="car" title="Claim" sub="Accident, injury and the other side" right={link('accidentDetails', 'Accident details')} />
          <div className={CB}>
            <div className="grid grid-cols-2 gap-x-[18px] gap-y-[12px]">
              <KV k="Accident">{accDesc || none}</KV>
              <KV k="Location">{place || none}</KV>
              <KV k="Injuries" full>{areas.length ? areas.join(' · ') : none}</KV>
              <KV k="Severity">{areas.length ? (c.injuryDetails.exceptionallySevere ? 'Exceptionally severe' : 'Standard') : none}</KV>
              <KV k="Claimant reg">{c.accidentDetails.claimantReg.trim() ? <span className="font-mono">{c.accidentDetails.claimantReg}</span> : none}</KV>
              <KV k="Defendant">{def.name.trim() ? <>{def.name}{def.reg.trim() && <span className="font-medium text-[12px] text-casevo-text-soft whitespace-nowrap"> · {def.reg}</span>}</> : none}</KV>
              <KV k="TPI insurer">{tpi.insurer.trim() ? <>{tpi.insurer}{tpi.referenceNumber.trim() && <span className="font-medium text-[12px] text-casevo-text-soft whitespace-nowrap"> · {tpi.referenceNumber}</span>}</> : none}</KV>
            </div>
          </div>
        </section>

        {/* ── 6. recent activity (History audit log) ── */}
        {canViewHistory && (
          <section className={`${CASE_CARD} lg:col-span-2`}>
            <CardHead icon="hist" title="Recent activity" sub="From the case history" right={link('history', 'Full history')} />
            <div className={CB}>
              {activityError && <p className="m-0 text-[13px] text-casevo-danger">Couldn’t load recent activity.</p>}
              {!activityError && activity === null && (
                // Shaped like the real rows (avatar + sentence + time line).
                <div aria-busy="true" aria-label="Loading recent activity">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} aria-hidden="true" className={`flex gap-[12px] ${i === 0 ? 'pt-[2px] pb-[10px]' : 'py-[10px] border-t border-casevo-border-soft'}`}>
                      <span className="w-7 h-7 rounded-full bg-casevo-input motion-safe:animate-pulse shrink-0" />
                      <div className="flex-1 min-w-0 text-[13px] leading-[1.45]">
                        <span className="block"><span className={`${SKELETON_BAR} h-3 w-64 max-w-full`} /></span>
                        <span className="block text-[11.5px]"><span className={`${SKELETON_BAR} h-2.5 w-20`} /></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {activity && activity.length === 0 && <p className="m-0 text-[13px] text-casevo-text-soft italic">No activity yet for this case.</p>}
              {activity && activity.map((e, i) => {
                const chip = chipFor(e.action)
                return (
                  <RowLink key={e.id} to="history" canOpen={canOpen} onOpen={onOpen} label="Open the full case history"
                    className={`flex gap-[12px] ${i === 0 ? 'pt-[2px] pb-[10px]' : 'py-[10px] border-t border-casevo-border-soft'}`}>
                    <ActorAvatar actorUserId={e.actorUserId} actorEmail={e.actorEmail} size="sm" />
                    <div className="flex-1 min-w-0 text-[13px] leading-[1.45]">
                      {renderActionSentence(e)}
                      <span className="block text-[11.5px] text-casevo-text-soft" title={new Date(e.createdAt).toLocaleString()}>{relativeTime(e.createdAt)}</span>
                    </div>
                    <span className={`self-start text-[10.5px] font-bold px-[7px] py-[2px] rounded-[5px] uppercase tracking-[0.04em] whitespace-nowrap ${chip.tone}`}>{chip.label}</span>
                  </RowLink>
                )
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

export default CaseOverview
